/**
 * PostHog browser analytics.
 *
 * Mirrors backend/shared/posthog_analytics.py: no-ops when POSTHOG_KEY is
 * unset (local dev), so init() is always safe to call. `getTracingHeaders()`
 * links browser events to backend events by passing the same distinct/session
 * ID PostHogMiddleware reads server-side (backend/shared/posthog_analytics.py).
 */
import posthog from 'posthog-js';
import { CONFIG } from '../config';

let initialized = false;

export function initPostHog(): void {
  if (initialized || !CONFIG.POSTHOG_KEY) return;
  posthog.init(CONFIG.POSTHOG_KEY, {
    api_host: CONFIG.POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    // Defense-in-depth: strip sensitive OAuth callback params from any
    // captured URL so tokens never reach PostHog even if a URL carrying
    // them slips through before redirect. Other query params are kept.
    before_send: (event) => {
      if (event && event.properties?.['$current_url']) {
        const url = new URL(event.properties['$current_url']);
        url.searchParams.delete('access_token');
        url.searchParams.delete('refresh_token');
        url.searchParams.delete('expires_in');
        url.searchParams.delete('verified');
        event.properties['$current_url'] = url.toString();
      }
      return event;
    },
  });
  // Staging and production share one PostHog project — tag every event so
  // they can be filtered/segmented apart in dashboards and insights.
  posthog.register({ environment: CONFIG.ENVIRONMENT });
  initialized = true;
}

export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.identify(userId, properties);
}

export function resetUser(): void {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Headers for backend/shared/posthog_analytics.py's PostHogMiddleware, so a
 * server-side capture() during this request is tagged with the same
 * distinct/session ID as the browser event stream.
 */
export function getTracingHeaders(): Record<string, string> {
  if (!initialized) return {};
  const headers: Record<string, string> = {};
  const distinctId = posthog.get_distinct_id();
  if (distinctId) headers['X-POSTHOG-DISTINCT-ID'] = distinctId;
  const sessionId = posthog.get_session_id();
  if (sessionId) headers['X-POSTHOG-SESSION-ID'] = sessionId;
  return headers;
}
